import { Generator, Purchasable, Resource, ResourceCost, UnitType } from '../../types/game-types';
import { useUpgradesStore } from '../zustand/useUpgradesStore';
import { useGameStatusStore } from '../zustand/useGameStatusStore';
import { useCurrencyStore } from '../zustand/useCurrencyStore';
import { costToBuyNItems, maxPurchasableItems } from '../helpers/calculate-cost';
import { getModifiedGeneratorCost } from '../helpers/get-modified-generator-cost';
import { useGeneratorContext } from '../generator-context';

export const useItemCost = (
  item: Purchasable,
  allowBuyMax: boolean = true,
): [ResourceCost, number, boolean, Resource[]] => {
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const buyMaxFromStore = useGameStatusStore((gameStatusState) => gameStatusState.buyMax);
  const entityType = useGeneratorContext();
  // Get currency values from the currency store (gold, mana, wood, ore, food)
  const gold = useCurrencyStore((state) => state.gold);
  const mana = useCurrencyStore((state) => state.mana);
  const wood = useCurrencyStore((state) => state.wood);
  const ore = useCurrencyStore((state) => state.ore);
  const food = useCurrencyStore((state) => state.food);
  const currencyValues: Record<string, number> = { gold, mana, wood, ore, food };

  const buyMax = buyMaxFromStore && allowBuyMax;
  const rate = item.costGrowthRate;

  const itemsLeftToPurchase = item.purchaseLimit ? item.purchaseLimit - item.purchased : Infinity;

  const itemCost =
    item.type === UnitType.UPGRADE
      ? item.cost
      : getModifiedGeneratorCost(upgrades[entityType], item as Generator);

  // Loop through each currency, and see which one is the lowest amount of items purchasable with that resource.
  // This is the max amount we can buy.
  const currencies = Object.keys(itemCost) as Resource[];
  const maxAmountOfItemsPurchaseable = currencies.reduce((currentMaxAmountOfItemsPurchaseable, currency): number => {
    const maxAmountOfItemsPurchaseable = maxPurchasableItems(
      currencyValues[currency] ?? 0,
      itemCost[currency]!,
      rate,
      item.purchased,
    );
    return Math.min(currentMaxAmountOfItemsPurchaseable, maxAmountOfItemsPurchaseable);
  }, itemsLeftToPurchase);

  const numberToPurchase = Math.max(1, buyMax ? maxAmountOfItemsPurchaseable : 1);

  // Loop through each currency, see how much of each resource is needed to purchase the item.
  const purchaseCost = currencies.reduce((costObj, currency) => {
    costObj[currency] = costToBuyNItems(itemCost[currency]!, item.costGrowthRate, item.purchased, numberToPurchase);
    return costObj;
  }, {} as ResourceCost);

  const currenciesWithoutEnoughFunds = currencies.filter((currency) => {
    return (currencyValues[currency] ?? 0) < purchaseCost[currency]!;
  });

  // Check each currency and each cost to see if too expensive or not
  const tooExpensive = currenciesWithoutEnoughFunds.length > 0;

  return [purchaseCost, !tooExpensive ? numberToPurchase : 0, tooExpensive, currenciesWithoutEnoughFunds];
};
